import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { trackAuth } from '../utils/analytics';

const OAuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const handleOAuthCallback = async () => {
      try {
        // Check if we have an error parameter
        const errorParam = searchParams.get('error');
        if (errorParam) {
          setError(`OAuth error: ${errorParam}`);
          trackAuth('failure', 'google');
          setTimeout(() => navigate('/'), 3000);
          return;
        }

        // Check if we have a token parameter (successful OAuth from backend)
        const token = searchParams.get('token');
        const encodedUserData = searchParams.get('user');
        
        if (token && encodedUserData) {
          console.log('OAuth callback received token and user data');
          
          try {
            // Decode user data from base64
            const userDataJson = atob(encodedUserData);
            const userData = JSON.parse(userDataJson);
            
            // Store authentication data
            localStorage.setItem('authToken', token);
            localStorage.setItem('userData', JSON.stringify(userData));
            
            // Set authentication state in the app
            // We'll need to trigger a global auth state update
            window.dispatchEvent(new CustomEvent('authStateChanged', {
              detail: { isAuthenticated: true, user: userData, token: token }
            }));
            
            // Track successful authentication
            trackAuth('success', 'google');
            
            // Redirect to home page
            navigate('/', { replace: true });
            return;
          } catch (decodeError) {
            console.error('Error decoding user data:', decodeError);
            setError('Authentication data processing failed. Please try again.');
            setTimeout(() => navigate('/'), 3000);
            return;
          }
        }

        // Check if we have a code parameter (fallback - direct OAuth flow)
        const code = searchParams.get('code');
        if (code) {
          console.log('OAuth callback received code:', code);
          
          // The OAuth success handler should have processed this
          // Let's check if we're authenticated by calling the auth status endpoint
          try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/status`, {
              credentials: 'include' // Include cookies
            });
            
            if (response.ok) {
              const authData = await response.json();
              if (authData.authenticated) {
                // User is authenticated, store the data
                localStorage.setItem('authToken', authData.token);
                localStorage.setItem('userData', JSON.stringify(authData.user));
                
                // Redirect to home page
                navigate('/', { replace: true });
                return;
              }
            }
          } catch (authError) {
            console.error('Error checking auth status:', authError);
          }
          
          // If we can't verify authentication, try to get user info directly
          // This is a fallback approach
          try {
            const userResponse = await fetch(`${process.env.REACT_APP_API_URL}/user/profile`, {
              credentials: 'include'
            });
            
            if (userResponse.ok) {
              const userData = await userResponse.json();
              localStorage.setItem('userData', JSON.stringify(userData));
              localStorage.setItem('authToken', 'authenticated'); // Placeholder
              navigate('/', { replace: true });
              return;
            }
          } catch (userError) {
            console.error('Error fetching user profile:', userError);
          }
          
          // If all else fails, redirect to home and let the header component handle auth check
          navigate('/', { replace: true });
        } else {
          // No code or error, redirect to home
          navigate('/', { replace: true });
        }
      } catch (error) {
        console.error('OAuth callback error:', error);
        setError('Authentication failed. Please try again.');
        setTimeout(() => navigate('/'), 3000);
      } finally {
        setIsProcessing(false);
      }
    };

    handleOAuthCallback();
  }, [searchParams, navigate]);

  if (error) {
    return (
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-6">
            <div className="alert alert-danger" role="alert">
              <h4 className="alert-heading">Authentication Error</h4>
              <p>{error}</p>
              <hr />
              <p className="mb-0">Redirecting to home page...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-5">
      <div className="row justify-content-center">
        <div className="col-md-6">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <h4 className="mt-3">Completing authentication...</h4>
            <p>Please wait while we complete your login.</p>
            {isProcessing && (
              <div className="mt-3">
                <small className="text-muted">Processing OAuth callback...</small>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OAuthCallback; 